/** @file Floating editor for token number + position. */
import { appStore } from "../core/state.js";
import { execute } from "../core/history.js";
import { updateTokenPropsCommand } from "../core/commands.js";
import { metersToPx } from "../core/coords.js";

/** @type {import("../core/play.js").PositionLabel[]} */
const POSITIONS = ["TW", "LA", "RL", "RM", "RR", "RA", "KM", null];

/** @type {HTMLDivElement | null} */
let panel = null;
/** @type {string | null} */
let currentTokenId = null;
/** @type {HTMLInputElement | null} */
let numberInput = null;
/** @type {HTMLSelectElement | null} */
let positionSelect = null;
/** @type {HTMLElement | null} */
let parentEl = null;

/**
 * @param {HTMLElement} parent
 */
export function mountTokenEditor(parent) {
  parentEl = parent;
  document.addEventListener("pointerdown", onOutsidePointer, true);
}

export function unmountTokenEditor() {
  close();
  document.removeEventListener("pointerdown", onOutsidePointer, true);
  parentEl = null;
}

/**
 * @param {string} tokenId
 */
export function openForToken(tokenId) {
  const state = appStore.get();
  const token = state.tokens.find((t) => t.id === tokenId);
  if (!token || !state.transform || token.kind === "ball") {
    return;
  }
  currentTokenId = tokenId;
  const pos = metersToPx(state.transform, { x: token.x, y: token.y });
  if (!panel) {
    panel = buildPanel();
    parentEl?.appendChild(panel);
  }
  if (numberInput) {
    numberInput.value = String(token.number);
  }
  if (positionSelect) {
    positionSelect.value = token.label ?? "";
  }
  panel.style.left = `${pos.px + 36}px`;
  panel.style.top = `${pos.py - 16}px`;
  panel.dataset.visible = "true";
  numberInput?.focus();
  numberInput?.select();
}

function buildPanel() {
  const el = document.createElement("div");
  el.className = "hb-token-editor";
  el.addEventListener("pointerdown", (e) => e.stopPropagation());

  const numberLabel = document.createElement("label");
  numberLabel.className = "hb-token-editor__field";
  const numberSpan = document.createElement("span");
  numberSpan.textContent = "Nr.";
  numberLabel.appendChild(numberSpan);
  numberInput = document.createElement("input");
  numberInput.type = "number";
  numberInput.min = "0";
  numberInput.max = "99";
  numberInput.className = "hb-token-editor__input";
  numberLabel.appendChild(numberInput);

  const positionLabel = document.createElement("label");
  positionLabel.className = "hb-token-editor__field";
  const positionSpan = document.createElement("span");
  positionSpan.textContent = "Pos.";
  positionLabel.appendChild(positionSpan);
  positionSelect = document.createElement("select");
  positionSelect.className = "hb-token-editor__input";
  for (const p of POSITIONS) {
    const opt = document.createElement("option");
    opt.value = p ?? "";
    opt.textContent = p ?? "—";
    positionSelect.appendChild(opt);
  }
  positionLabel.appendChild(positionSelect);

  const actions = document.createElement("div");
  actions.className = "hb-token-editor__actions";
  const ok = document.createElement("button");
  ok.type = "button";
  ok.className = "hb-btn hb-btn--primary";
  ok.textContent = "✓";
  ok.addEventListener("click", commit);
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "hb-btn";
  cancel.textContent = "✕";
  cancel.addEventListener("click", close);
  actions.appendChild(ok);
  actions.appendChild(cancel);

  el.appendChild(numberLabel);
  el.appendChild(positionLabel);
  el.appendChild(actions);

  numberInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  });
  positionSelect.addEventListener("change", commit);

  return el;
}

function commit() {
  if (!currentTokenId || !numberInput || !positionSelect) {
    close();
    return;
  }
  const state = appStore.get();
  const token = state.tokens.find((t) => t.id === currentTokenId);
  if (!token) {
    close();
    return;
  }
  const nextNumberRaw = parseInt(numberInput.value, 10);
  const nextNumber = Number.isFinite(nextNumberRaw)
    ? Math.min(99, Math.max(0, nextNumberRaw))
    : token.number;
  const nextLabelRaw = positionSelect.value;
  const nextLabel = /** @type {import("../core/play.js").PositionLabel} */ (
    nextLabelRaw === "" ? null : nextLabelRaw
  );

  if (nextNumber !== token.number || nextLabel !== token.label) {
    execute(
      updateTokenPropsCommand(
        currentTokenId,
        { number: token.number, label: token.label },
        { number: nextNumber, label: nextLabel },
      ),
    );
  }
  close();
}

function close() {
  currentTokenId = null;
  if (panel) {
    panel.dataset.visible = "false";
  }
}

/** @param {PointerEvent} e */
function onOutsidePointer(e) {
  if (!panel || panel.dataset.visible !== "true") {
    return;
  }
  const target = /** @type {Node | null} */ (e.target);
  if (target && panel.contains(target)) {
    return;
  }
  close();
}
